import { useEffect } from "react";
import { captureZoomAnchor } from "./calendar-zoom-anchor";

// Mouse-driven zoom + pan for the calendar grids. Attaches its listeners to a single
// wrapper element (so they fire wherever the cursor is) but reads/writes scroll on the
// *active view's* viewport, which the view registers itself (a view can contain several
// nested ScrollAreas — e.g. each event card — so resolving the viewport from the wrapper
// is unreliable; the parent hands us the right one via getViewport).
//
//   • Zoom   — ctrl/cmd + wheel (also macOS trackpad pinch, which arrives as a wheel event
//              with ctrlKey === true). Two axes:
//                – plain  → scale BOTH axes (vertical hour rows + horizontal columns)
//                – +Shift → vertical only
//                – +Alt   → horizontal only
//              Month has no horizontal axis (horizontalRange null) so alt-only is a no-op
//              there. Anchored at the cursor: the point under the pointer stays put. The
//              actual scroll compensation is done by each view's zoom-change effects, which
//              read the anchor we stash in zoomAnchorRef.
//   • Pan    — drag with the left button while cmd/ctrl is held, or with the middle button.
//              "Grab the canvas" panning: we move the viewport's scrollLeft/scrollTop.
//
// React's JSX onWheel is registered passively, so preventDefault() is a no-op there; the
// wheel and middle-button listeners must be attached imperatively with { passive: false }.

// How aggressively the wheel zooms. Exponential so it is symmetric in/out and a single
// notch is a small, smooth step.
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

export function useCalendarZoomPan({
  wrapperRef,
  getViewport,
  activeView,
  zoomAnchorRef,
  getZoom, // () => ({ vertical: number, horizontal: number|null })
  onZoom, // (updates: { vertical?: number, horizontal?: number }) => void
  verticalMin = 50,
  verticalMax = 600,
  horizontalMin = null, // null when the active view has no horizontal zoom (month)
  horizontalMax = null,
  enabled = true,
}) {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !enabled) {
      return undefined;
    }

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const hasHorizontal = horizontalMin != null && horizontalMax != null;

    // ----- Zoom (ctrl/cmd + wheel, axis selected by shift/alt) -------------------------
    const onWheel = (event) => {
      if (!(event.ctrlKey || event.metaKey)) {
        return; // plain wheel scrolls normally
      }
      const viewport = getViewport();
      if (!viewport) {
        return;
      }
      event.preventDefault(); // works because the listener is { passive: false }

      // Shift => vertical only, Alt => horizontal only, neither => both.
      const wantVertical = !event.altKey;
      const wantHorizontal = !event.shiftKey && hasHorizontal;
      if (!wantVertical && !wantHorizontal) {
        return; // shift+alt with no horizontal axis, etc.
      }

      // macOS (and Windows) swap the wheel delta from deltaY into deltaX while Shift is held,
      // so reading deltaY alone makes Shift+wheel a no-op. Use whichever axis carries the
      // scroll; the modifier keys (read above) select the zoom axis independently.
      const rawDelta =
        Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (rawDelta === 0) {
        return;
      }
      const factor = Math.exp(-rawDelta * WHEEL_ZOOM_SENSITIVITY);
      const zoom = getZoom();
      const updates = {};
      if (wantVertical && zoom.vertical != null) {
        const next = clamp(Math.round(zoom.vertical * factor), verticalMin, verticalMax);
        if (next !== zoom.vertical) {
          updates.vertical = next;
        }
      }
      if (wantHorizontal && zoom.horizontal != null) {
        const next = clamp(Math.round(zoom.horizontal * factor), horizontalMin, horizontalMax);
        if (next !== zoom.horizontal) {
          updates.horizontal = next;
        }
      }
      if (updates.vertical == null && updates.horizontal == null) {
        return; // nothing changed (e.g. bouncing at a clamp)
      }

      // Record where the cursor is, in viewport + content space, BEFORE the zoom changes.
      // Each axis's compensation effect consumes its half and clears its flag; the last one
      // out nulls the anchor.
      const rect = viewport.getBoundingClientRect();
      const pointerOffsetX = event.clientX - rect.left;
      const pointerOffsetY = event.clientY - rect.top;
      zoomAnchorRef.current = {
        view: activeView,
        pointerOffsetX,
        pointerOffsetY,
        contentX: viewport.scrollLeft + pointerOffsetX,
        contentY: viewport.scrollTop + pointerOffsetY,
        prevScrollWidth: viewport.scrollWidth,
        prevScrollHeight: viewport.scrollHeight,
        // The grid-relative reading of the same point, measured off real rects.
        // This is what the views actually restore to; the scroll-extent fields
        // above remain the fallback for grids that can't be measured.
        measured: captureZoomAnchor(viewport, event.clientX, event.clientY),
        axes: { vertical: updates.vertical != null, horizontal: updates.horizontal != null },
      };
      onZoom(updates);
    };

    // ----- Pan (cmd/ctrl + left drag, or middle-button drag) ---------------------------
    let panning = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;
    let panViewport = null;
    let previousUserSelect = "";

    const onPointerMove = (event) => {
      if (!panning || !panViewport) {
        return;
      }
      event.preventDefault();
      panViewport.scrollLeft = startLeft - (event.clientX - startX);
      panViewport.scrollTop = startTop - (event.clientY - startY);
    };

    const endPan = () => {
      if (!panning) {
        return;
      }
      panning = false;
      panViewport = null;
      wrapper.style.cursor = "";
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endPan, true);
      window.removeEventListener("pointercancel", endPan, true);
    };

    const onPointerDownCapture = (event) => {
      const isModifierLeft = event.button === 0 && (event.metaKey || event.ctrlKey);
      const isMiddle = event.button === 1;
      if (!isModifierLeft && !isMiddle) {
        return;
      }
      const viewport = getViewport();
      if (!viewport) {
        return;
      }
      // Capture phase + stopPropagation so dnd-kit (which listens for a plain left press on
      // appointment elements) never starts a move-drag when our pan gesture begins on one.
      event.stopPropagation();
      event.preventDefault();

      panning = true;
      panViewport = viewport;
      startX = event.clientX;
      startY = event.clientY;
      startLeft = viewport.scrollLeft;
      startTop = viewport.scrollTop;
      wrapper.style.cursor = "grabbing";
      previousUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = "none";

      window.addEventListener("pointermove", onPointerMove, { passive: false });
      window.addEventListener("pointerup", endPan, true);
      window.addEventListener("pointercancel", endPan, true);
    };

    // Middle-button press otherwise opens the OS/Chromium autoscroll affordance.
    const onMouseDown = (event) => {
      if (event.button === 1) {
        event.preventDefault();
      }
    };

    wrapper.addEventListener("wheel", onWheel, { passive: false });
    wrapper.addEventListener("pointerdown", onPointerDownCapture, true);
    wrapper.addEventListener("mousedown", onMouseDown, { passive: false });

    return () => {
      wrapper.removeEventListener("wheel", onWheel);
      wrapper.removeEventListener("pointerdown", onPointerDownCapture, true);
      wrapper.removeEventListener("mousedown", onMouseDown);
      endPan();
    };
  }, [
    wrapperRef,
    getViewport,
    activeView,
    zoomAnchorRef,
    getZoom,
    onZoom,
    verticalMin,
    verticalMax,
    horizontalMin,
    horizontalMax,
    enabled,
  ]);
}
